/**
 * Order detail page with refund and status update functionality.
 */

import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api, ApiRequestError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { formatCurrency, formatDateTime, getStatusConfig } from '@/lib/formatters'
import type { Order } from '@/hooks/useOrders'

interface OrderHistory {
  id: number
  action: string
  details: string
  created_at: string
  created_by: string
}

export default function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const [order, setOrder] = useState<Order | null>(null)
  const [history, setHistory] = useState<OrderHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRefundOpen, setIsRefundOpen] = useState(false)
  const [isStatusOpen, setIsStatusOpen] = useState(false)
  const [refundData, setRefundData] = useState({ amount: '', reason: '' })
  const [statusData, setStatusData] = useState({ status: '', reason: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const fetchOrder = async () => {
    if (!orderId) return

    try {
      setLoading(true)
      const response = await api.get<Order>(`/orders/${orderId}`)
      setOrder(response.data)

      // Also fetch history
      try {
        const historyResponse = await api.get<OrderHistory[]>(`/orders/${orderId}/history`)
        setHistory(historyResponse.data)
      } catch {
        // History might not be implemented, that's ok
      }

      setError(null)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to load order')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrder()
  }, [orderId])

  const handleRefund = async () => {
    if (!order) return

    const amount = parseFloat(refundData.amount)
    if (isNaN(amount) || amount <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' })
      return
    }
    if (amount * 100 > order.amount) {
      toast({ title: 'Error', description: 'Refund amount cannot exceed order amount', variant: 'destructive' })
      return
    }
    if (!refundData.reason.trim()) {
      toast({ title: 'Error', description: 'Please provide a reason', variant: 'destructive' })
      return
    }

    setIsSubmitting(true)
    try {
      await api.post(`/orders/${orderId}/refund`, {
        amount: Math.round(amount * 100),
        reason: refundData.reason,
      })
      toast({ title: 'Success', description: 'Refund initiated successfully', variant: 'success' })
      setIsRefundOpen(false)
      setRefundData({ amount: '', reason: '' })
      fetchOrder()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof ApiRequestError ? err.message : 'Failed to process refund',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStatusUpdate = async () => {
    if (!statusData.status) {
      toast({ title: 'Error', description: 'Please select a status', variant: 'destructive' })
      return
    }
    if (!statusData.reason.trim()) {
      toast({ title: 'Error', description: 'Please provide a reason', variant: 'destructive' })
      return
    }

    setIsSubmitting(true)
    try {
      await api.put(`/orders/${orderId}/status`, statusData)
      toast({ title: 'Success', description: 'Status updated successfully', variant: 'success' })
      setIsStatusOpen(false)
      setStatusData({ status: '', reason: '' })
      fetchOrder()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof ApiRequestError ? err.message : 'Failed to update status',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div>
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/orders">Back to Orders</Link>
        </Button>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error || 'Order not found'}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={fetchOrder}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const statusConfig = getStatusConfig(order.status)

  return (
    <div>
      <Button variant="ghost" asChild className="mb-4">
        <Link to="/orders">Back to Orders</Link>
      </Button>

      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold">Order {order.order_no}</h1>
          <p className="text-muted-foreground mt-2">
            Created {formatDateTime(order.created_at)}
          </p>
        </div>
        <Badge className={statusConfig.className} style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
          {statusConfig.label}
        </Badge>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Order Info */}
        <Card>
          <CardHeader>
            <CardTitle>Order Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Order No</span>
              <span className="font-mono">{order.order_no}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-semibold">{formatCurrency(order.amount, order.currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <Badge className={statusConfig.className}>{statusConfig.label}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Channel</span>
              <span>{order.payment_channel || '-'}</span>
            </div>
            {order.paid_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Paid At</span>
                <span>{formatDateTime(order.paid_at)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Info */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name</span>
              <span>{order.customer_name || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email</span>
              <span>{order.customer_email || '-'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Order Items */}
        {order.items && order.items.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Order Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.items.map((item) => (
                  <div key={item.id} className="flex justify-between items-center p-3 border rounded">
                    <div>
                      <div className="font-medium">{item.sku_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatCurrency(item.unit_price, order.currency)} x {item.quantity}
                      </div>
                    </div>
                    <div className="font-semibold">
                      {formatCurrency(item.total_price, order.currency)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
            <CardDescription>Manage this order</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {order.status === 'paid' && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setIsRefundOpen(true)}
              >
                Process Refund
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setIsStatusOpen(true)}
            >
              Update Status
            </Button>
          </CardContent>
        </Card>

        {/* History */}
        {history.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {history.map((entry) => (
                  <div key={entry.id} className="border-l-2 border-muted pl-4 py-2">
                    <div className="font-medium">{entry.action}</div>
                    <div className="text-sm text-muted-foreground">{entry.details}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatDateTime(entry.created_at)} by {entry.created_by}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Refund Dialog */}
      <Dialog open={isRefundOpen} onOpenChange={setIsRefundOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
            <DialogDescription>
              Enter the refund amount and reason. Maximum refundable: {formatCurrency(order.amount, order.currency)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="refund-amount">Amount ({order.currency})</Label>
              <Input
                id="refund-amount"
                type="number"
                step="0.01"
                min="0"
                max={order.amount / 100}
                value={refundData.amount}
                onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="refund-reason">Reason</Label>
              <textarea
                id="refund-reason"
                value={refundData.reason}
                onChange={(e) => setRefundData({ ...refundData, reason: e.target.value })}
                placeholder="Reason for refund..."
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRefundOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRefund} disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Process Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={isStatusOpen} onOpenChange={setIsStatusOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Order Status</DialogTitle>
            <DialogDescription>
              Manually update the order status. This will be logged in the order history.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select
                value={statusData.status}
                onValueChange={(value) => setStatusData({ ...statusData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status-reason">Reason</Label>
              <textarea
                id="status-reason"
                value={statusData.reason}
                onChange={(e) => setStatusData({ ...statusData, reason: e.target.value })}
                placeholder="Reason for status change..."
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate} disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
